import React from "react";

// Three shape families only:
//  1. priority  -> angular chip with a 3px left bar
//  2. inspection -> stamped mark, optionally with a date
//  3. everything else -> text with a dot
// Filled + saturated means "wrong right now". Anything confirming that things
// are fine is unfilled. The four functional colour pairs are unchanged.

// Dutch labels for shared enums used across the UI.
export const LOCATION_TYPE_LABELS = {
  truckwash: "Truckwash",
  restaurant: "Restaurant",
  kantoor: "Kantoor",
  opslag: "Opslag",
  pand: "Pand",
};

export const LOCATION_STATUS_LABELS = {
  active: "Actief",
  planned: "Gepland",
  closed: "Gesloten",
};

export const OWNERSHIP_LABELS = {
  eigendom: "Eigendom",
  huur: "Huur",
};

export const ROLE_LABELS = {
  owner: "Eigenaar",
  td: "TD",
  management: "Management",
};

// ---------------- shared primitives ----------------
const TONE = {
  groen: { fill: "bg-goed-vlak text-goed-tekst border-goed-rand", dot: "bg-goed-rand" },
  oranje: { fill: "bg-letop-vlak text-letop-tekst border-letop-rand", dot: "bg-letop-tekst" },
  geel: { fill: "bg-wacht-vlak text-wacht-tekst border-wacht-rand", dot: "bg-wacht-tekst" },
  rood: { fill: "bg-fout-vlak text-fout-tekst border-fout-rand", dot: "bg-fout-tekst" },
  neutraal: { fill: "bg-rust-vlak text-rust-tekst border-rust-rand", dot: "bg-inkt-zacht" },
};

const nlDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
};

// The word inside a condition badge is always the LABEL step of the type scale:
// 11px, 600, +0.07em, uppercase. Mixing sentence case and uppercase inside one
// card was the thing that made three consecutive rows read as three systems.
// `word={false}` is for a plain fact (a category, a name), never for a state.
const WORD = "text-label font-semibold uppercase tracking-[0.07em]";
const FACT = "text-data font-medium";

// Family 3: text with a dot. Unfilled = nothing to act on, so the text is quiet
// ink: "Actief" must never compete with a real fault two rows down.
export const DotLabel = ({ tone = "neutraal", children, testid, word = false }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1.5 text-inkt-zacht ${word ? WORD : FACT}`}>
    <span className={`h-2 w-2 shrink-0 rounded-full ${TONE[tone].dot}`} aria-hidden="true" />
    {children}
  </span>
);

// Family 3 (bad state): the same row position, but filled so it pulls the eye.
export const FilledLabel = ({ tone = "rood", children, testid, className = "", word = true }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 ${word ? WORD : FACT} ${TONE[tone].fill} ${className}`}
  >
    {children}
  </span>
);

export const StatusBadge = ({ status }) => {
  const tone = status === "active" ? "groen" : "neutraal";
  return (
    <DotLabel tone={tone} word testid={`status-badge-${status}`}>
      {LOCATION_STATUS_LABELS[status] || status}
    </DotLabel>
  );
};

export const OwnershipBadge = ({ ownership }) => {
  if (!ownership) return null;
  return <DotLabel tone="neutraal">{OWNERSHIP_LABELS[ownership]}</DotLabel>;
};

export const RoleBadge = ({ role }) => (
  <span
    data-testid="role-badge"
    className="inline-flex items-center rounded-sm border border-border bg-muted px-2 py-0.5 text-label font-semibold uppercase tracking-[0.07em] text-muted-foreground"
  >
    {ROLE_LABELS[role] || role}
  </span>
);

// ---------------- Asset register ----------------
export const ASSET_STATUS_LABELS = {
  actief: "Actief",
  defect: "Defect",
  in_reparatie: "In reparatie",
  vervangen: "Vervangen",
  afgevoerd: "Afgevoerd",
};

export const RESPONSIBILITY_LABELS = {
  ons: "Ons",
  verhuurder: "Verhuurder",
};

const REPLACEMENT_WORD = {
  groen: "OP TIJD",
  oranje: "BINNENKORT",
  rood: "OVER",
  onbekend: "ONBEKEND",
};

const REPLACEMENT_BAR = {
  groen: "bg-goed-tekst",
  oranje: "bg-letop-tekst",
  rood: "bg-fout-tekst",
  onbekend: "bg-inkt-zacht",
};

// Colour + number + word, so it stays readable for red-green colourblind eyes.
export const ReplacementBadge = ({ status, date }) => {
  const key = REPLACEMENT_WORD[status] ? status : "onbekend";
  const urgent = key === "rood" || key === "oranje";
  const tone = key === "onbekend" ? "neutraal" : key;
  return (
    <span
      data-testid={`replacement-badge-${status}`}
      className={`inline-flex items-stretch gap-2 rounded-sm border py-0.5 pr-2 text-data ${
        urgent ? TONE[tone].fill : "border-transparent bg-transparent text-foreground"
      }`}
    >
      <span className={`w-[3px] shrink-0 rounded-sm ${REPLACEMENT_BAR[key]}`} aria-hidden="true" />
      {date && key !== "onbekend" && <span className="font-mono font-medium tabular-nums">{nlDate(date)}</span>}
      <span className="text-label font-semibold uppercase tracking-[0.07em]">{REPLACEMENT_WORD[key]}</span>
    </span>
  );
};

export const AssetStatusBadge = ({ status }) => {
  if (status === "defect") return <FilledLabel tone="rood">Defect</FilledLabel>;
  if (status === "in_reparatie") return <FilledLabel tone="oranje">In reparatie</FilledLabel>;
  const tone = status === "actief" ? "groen" : "neutraal";
  return <DotLabel tone={tone} word>{ASSET_STATUS_LABELS[status] || status}</DotLabel>;
};

export const formatNlDate = nlDate;

export const formatEuro = (v) =>
  v === null || v === undefined || v === ""
    ? ""
    : new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(v);

// ---------------- Fault register ----------------
export const PRIORITY_LABELS = {
  P1: "P1 kritiek",
  P2: "P2 hoog",
  P3: "P3 normaal",
  P4: "P4 laag",
};

export const FAULT_STATUS_LABELS = {
  open: "Open",
  in_behandeling: "In behandeling",
  wacht_op_onderdelen: "Wacht op onderdelen",
  gemeld_bij_verhuurder: "Gemeld bij verhuurder",
  opgelost: "Opgelost",
  afgewezen: "Afgewezen",
};

const PRIORITY_TONE = { P1: "rood", P2: "oranje", P3: "neutraal", P4: "neutraal" };
const PRIORITY_BAR = { P1: "bg-fout-tekst", P2: "bg-letop-tekst", P3: "bg-inkt-zacht", P4: "bg-lijn" };

// Family 1: angular chip with a left bar. P1/P2 filled, P3/P4 not.
export const PriorityBadge = ({ priority }) => {
  const key = PRIORITY_TONE[priority] ? priority : "P4";
  const urgent = key === "P1" || key === "P2";
  return (
    <span
      data-testid={`priority-badge-${priority}`}
      className={`inline-flex items-stretch gap-2 rounded-none border py-0.5 pr-2 text-label font-semibold uppercase tracking-[0.07em] ${
        urgent ? TONE[PRIORITY_TONE[key]].fill : "border-border bg-white text-foreground"
      }`}
    >
      <span className={`w-[3px] shrink-0 ${PRIORITY_BAR[key]}`} aria-hidden="true" />
      {PRIORITY_LABELS[priority] || priority}
    </span>
  );
};

const FAULT_STATUS_TONE = {
  open: "rood",
  wacht_op_onderdelen: "oranje",
  gemeld_bij_verhuurder: "geel",
  in_behandeling: "oranje",
};

// ---------------- Werkbonnen (round 24) ----------------
export const WERKBON_STATUS_LABELS = {
  voorbereid: "Voorbereid",
  uitgegeven: "Uitgegeven",
  gereed: "Gereed",
  geannuleerd: "Geannuleerd",
};

export const WERKBON_SOORT_LABELS = {
  storing: "Storing",
  correctief: "Correctief",
  preventief: "Preventief",
  keuring: "Keuring",
  overig: "Overig",
};

// voorbereid is parked/planned (cold blue), uitgegeven is live work, gereed is
// genuinely done. Nothing here is "wrong right now", so no FilledLabel.
const WERKBON_STATUS_TONE = { voorbereid: "geel", uitgegeven: "oranje", gereed: "groen", geannuleerd: "neutraal" };

export const WerkbonStatusBadge = ({ status }) => (
  <DotLabel tone={WERKBON_STATUS_TONE[status] || "neutraal"} word testid={`werkbon-status-badge-${status}`}>
    {WERKBON_STATUS_LABELS[status] || status}
  </DotLabel>
);

export const FaultStatusBadge = ({ status }) => {
  const label = FAULT_STATUS_LABELS[status] || status;
  const tone = FAULT_STATUS_TONE[status];
  if (tone) {
    return (
      <FilledLabel tone={tone} testid={`fault-status-badge-${status}`}>
        {label}
      </FilledLabel>
    );
  }
  return (
    <DotLabel tone={status === "opgelost" ? "groen" : "neutraal"} word testid={`fault-status-badge-${status}`}>
      {label}
    </DotLabel>
  );
};

// ---------------- Inspections ----------------
export const INSPECTION_STATUS_LABELS = {
  geldig: "Geldig",
  binnenkort: "Binnenkort",
  te_controleren: "Te controleren",
  verlopen: "Verlopen",
  onbekend: "Onbekend",
};

// Family 2: stamped mark. `verlopen` is uppercase and double-ruled so it never
// reads the same as `te_controleren`, even though both are red.
export const InspectionStatusBadge = ({ status, date }) => {
  const label = INSPECTION_STATUS_LABELS[status] || status;
  const stamp = "inline-flex items-center gap-1.5 rounded-none border px-2 py-0.5 text-label font-semibold uppercase tracking-[0.07em]";
  if (status === "verlopen") {
    return (
      <span
        data-testid={`inspection-status-badge-${status}`}
        className={`${stamp} border-[3px] border-double ${TONE.rood.fill}`}
      >
        {label}
        {date && <span className="font-mono tabular-nums">{nlDate(date)}</span>}
      </span>
    );
  }
  if (status === "te_controleren" || status === "binnenkort") {
    const tone = status === "binnenkort" ? "oranje" : "rood";
    return (
      <span
        data-testid={`inspection-status-badge-${status}`}
        className={`${stamp} ${TONE[tone].fill}`}
      >
        {label}
        {date && <span className="font-mono tabular-nums">{nlDate(date)}</span>}
      </span>
    );
  }
  return (
    <DotLabel tone={status === "geldig" ? "groen" : "neutraal"} word testid={`inspection-status-badge-${status}`}>
      {label}
      {date && <span className="font-mono text-label tabular-nums text-muted-foreground">{nlDate(date)}</span>}
    </DotLabel>
  );
};
